import { useState, useEffect } from 'react';
import { Scale, HelpCircle, Download, Plus, Trash2, LineChart } from 'lucide-react';
import {
  LineChart as RechartsLineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import ToolPage from '@/components/ToolPage';

interface Debt {
  id: string;
  name: string;
  balance: number;
  interestRate: number;
  minimumPayment: number;
}

interface PayoffStrategy {
  name: string;
  description: string;
  monthsToPayoff: number;
  totalInterest: number;
  monthlyPayments: {
    month: number;
    payment: number;
    principal: number;
    interest: number;
    remainingBalance: number;
  }[];
}

const DEBT_STORAGE_KEY = 'hoito-debt-payoff-debts';

export default function DebtPayoffPlanner() {
  const [debts, setDebts] = useState<Debt[]>(() => {
    try {
      const stored = localStorage.getItem(DEBT_STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });
  const [showAddModal, setShowAddModal] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [extraPayment, setExtraPayment] = useState<string>('0');
  const [showAmortization, setShowAmortization] = useState(false);
  const [selectedStrategy, setSelectedStrategy] = useState<'avalanche' | 'snowball'>('avalanche');
  const [newDebt, setNewDebt] = useState<Debt>({
    id: '',
    name: '',
    balance: 0,
    interestRate: 0,
    minimumPayment: 0
  });

  const calculatePayoffStrategy = (strategy: 'avalanche' | 'snowball'): PayoffStrategy => {
    if (debts.length === 0) {
      return {
        name: strategy === 'avalanche' ? 'Debt Avalanche' : 'Debt Snowball',
        description: '',
        monthsToPayoff: 0,
        totalInterest: 0,
        monthlyPayments: []
      };
    }

    // Sort debts based on strategy
    const sortedDebts = [...debts].sort((a, b) => {
      if (strategy === 'avalanche') {
        return b.interestRate - a.interestRate;
      } else {
        return a.balance - b.balance;
      }
    });

    let totalMonths = 0;
    let totalInterestPaid = 0;
    const monthlyPayments = [];
    const workingDebts = sortedDebts.map(debt => ({ ...debt }));
    const extraAmount = Number(extraPayment) || 0;

    while (workingDebts.some(debt => debt.balance > 0)) {
      totalMonths++;
      let monthPayment = 0;
      let monthPrincipal = 0;
      let monthInterest = 0;
      let remainingExtra = extraAmount;

      // Process each debt
      for (let i = 0; i < workingDebts.length; i++) {
        const debt = workingDebts[i];
        if (debt.balance <= 0) continue;

        // Calculate interest
        const monthlyInterest = (debt.interestRate / 100 / 12) * debt.balance;
        totalInterestPaid += monthlyInterest;
        monthInterest += monthlyInterest;

        // Calculate payment
        let payment = debt.minimumPayment;
        if (i === 0) { // Apply extra payment to first debt in strategy
          payment += remainingExtra;
        }

        // Adjust payment if it's more than remaining balance + interest
        const totalDue = debt.balance + monthlyInterest;
        if (payment > totalDue) {
          payment = totalDue;
          if (i === 0) {
            remainingExtra = payment - totalDue;
          }
        }

        // Update balances
        debt.balance = Math.max(0, debt.balance + monthlyInterest - payment);
        monthPayment += payment;
        monthPrincipal += payment - monthlyInterest;
      }

      monthlyPayments.push({
        month: totalMonths,
        payment: monthPayment,
        principal: monthPrincipal,
        interest: monthInterest,
        remainingBalance: workingDebts.reduce((sum, debt) => sum + debt.balance, 0)
      });
    }

    return {
      name: strategy === 'avalanche' ? 'Debt Avalanche' : 'Debt Snowball',
      description: strategy === 'avalanche' 
        ? 'Pays off highest interest debt first (saves the most money)'
        : 'Pays off smallest balance first (provides quick wins)',
      monthsToPayoff: totalMonths,
      totalInterest: totalInterestPaid,
      monthlyPayments
    };
  };

  const handleAddDebt = () => {
    if (!newDebt.name || newDebt.balance <= 0 || newDebt.interestRate <= 0 || newDebt.minimumPayment <= 0) {
      return;
    }

    setDebts([...debts, { ...newDebt, id: `debt-${Date.now()}` }]);
    setNewDebt({
      id: '',
      name: '',
      balance: 0,
      interestRate: 0,
      minimumPayment: 0
    });
    setShowAddModal(false);
  };

  useEffect(() => {
    localStorage.setItem(DEBT_STORAGE_KEY, JSON.stringify(debts));
  }, [debts]);

  const handleDeleteDebt = (id: string) => {
    setDebts(debts.filter(debt => debt.id !== id));
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const downloadPayoffPlan = () => {
    const strategy = calculatePayoffStrategy(selectedStrategy);

    let csv = 'Debt Payoff Plan\n\n';
    
    // Current Debts
    csv += 'Current Debts\n';
    csv += 'Name,Balance,Interest Rate,Minimum Payment\n';
    debts.forEach(debt => {
      csv += `${debt.name},${debt.balance},${debt.interestRate}%,${debt.minimumPayment}\n`;
    });

    // Monthly Payments
    csv += `\n${strategy.name} Strategy\n`;
    csv += 'Month,Payment,Principal,Interest,Remaining Balance\n';
    strategy.monthlyPayments.forEach(payment => {
      csv += `${payment.month},${payment.payment},${payment.principal},${payment.interest},${payment.remainingBalance}\n`;
    });

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'debt-payoff-plan.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const strategy = calculatePayoffStrategy(selectedStrategy);
  const totalDebt = debts.reduce((sum, debt) => sum + debt.balance, 0);
  const totalMinimumPayment = debts.reduce((sum, debt) => sum + debt.minimumPayment, 0);

  return (
    <ToolPage title="Debt Payoff Planner" description="Create a plan to become debt-free faster" icon={Scale}>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Debts List */}
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-sm p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-gray-900">Your Debts</h2>
                <button
                  onClick={() => setShowAddModal(true)}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-brand-700 bg-brand-50 rounded-lg hover:bg-brand-100 transition-colors"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Debt
                </button>
              </div>

              {showAddModal && (
                <div className="space-y-4 mb-6">
                  <input
                    type="text"
                    value={newDebt.name}
                    onChange={(e) => setNewDebt({ ...newDebt, name: e.target.value })}
                    placeholder="Debt name"
                    className="w-full px-4 py-3 rounded-lg bg-gray-50 border border-gray-200 focus:border-brand-500 focus:ring-2 focus:ring-brand-200 outline-none transition-all"
                  />

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Current Balance
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">£</span>
                        <input
                          type="number"
                          min="0"
                          value={newDebt.balance || ''}
                          onChange={(e) => setNewDebt({ ...newDebt, balance: Number(e.target.value) })}
                          className="w-full pl-8 pr-4 py-3 rounded-lg bg-gray-50 border border-gray-200 focus:border-brand-500 focus:ring-2 focus:ring-brand-200 outline-none transition-all"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Interest Rate
                      </label>
                      <div className="relative">
                        <input
                          type="number"
                          min="0"
                          step="0.1"
                          value={newDebt.interestRate || ''}
                          onChange={(e) => setNewDebt({ ...newDebt, interestRate: Number(e.target.value) })}
                          className="w-full pr-8 py-3 px-4 rounded-lg bg-gray-50 border border-gray-200 focus:border-brand-500 focus:ring-2 focus:ring-brand-200 outline-none transition-all"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">%</span>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Minimum Payment
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">£</span>
                        <input
                          type="number"
                          min="0"
                          value={newDebt.minimumPayment || ''}
                          onChange={(e) => setNewDebt({ ...newDebt, minimumPayment: Number(e.target.value) })}
                          className="w-full pl-8 pr-4 py-3 rounded-lg bg-gray-50 border border-gray-200 focus:border-brand-500 focus:ring-2 focus:ring-brand-200 outline-none transition-all"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={() => setShowAddModal(false)}
                      className="px-4 py-2 text-gray-600 hover:text-gray-800"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleAddDebt}
                      className="px-4 py-2 bg-brand-900 text-white rounded-lg hover:bg-brand-800 transition-colors"
                    >
                      Add Debt
                    </button>
                  </div>
                </div>
              )}

              {debts.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-gray-500">No debts added yet</p>
                  <button
                    onClick={() => setShowAddModal(true)}
                    className="mt-2 text-brand-700 hover:text-brand-800 font-medium"
                  >
                    Add your first debt
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {debts.map(debt => (
                    <div
                      key={debt.id}
                      className="flex items-center justify-between p-4 bg-gray-50 rounded-lg group"
                    >
                      <div>
                        <h3 className="font-medium text-gray-900">{debt.name}</h3>
                        <p className="text-sm text-gray-500">
                          {debt.interestRate}% APR • Min: {formatCurrency(debt.minimumPayment)}/mo
                        </p>
                      </div>
                      <div className="flex items-center space-x-4">
                        <span className="font-medium text-gray-900">
                          {formatCurrency(debt.balance)}
                        </span>
                        <button
                          onClick={() => handleDeleteDebt(debt.id)}
                          className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-600 transition-all"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}

                  <div className="mt-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Extra Monthly Payment
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">£</span>
                      <input
                        type="number"
                        min="0"
                        value={extraPayment}
                        onChange={(e) => setExtraPayment(e.target.value)}
                        placeholder="Additional monthly payment"
                        className="w-full pl-8 pr-4 py-3 rounded-lg bg-gray-50 border border-gray-200 focus:border-brand-500 focus:ring-2 focus:ring-brand-200 outline-none transition-all"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {debts.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-lg font-semibold text-gray-900">Summary</h2>
                  <button
                    onClick={downloadPayoffPlan}
                    className="inline-flex items-center px-4 py-2 text-sm font-medium text-brand-700 bg-brand-50 rounded-lg hover:bg-brand-100 transition-colors"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download Plan
                  </button>
                </div>

                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Total Debt</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {formatCurrency(totalDebt)}
                    </p>
                  </div>

                  <div>
                    <p className="text-sm text-gray-500 mb-1">Monthly Minimum Payments</p>
                    <p className="text-2xl font-bold text-brand-700">
                      {formatCurrency(totalMinimumPayment)}
                    </p>
                  </div>

                  {Number(extraPayment) > 0 && (
                    <div>
                      <p className="text-sm text-gray-500 mb-1">Extra Monthly Payment</p>
                      <p className="text-2xl font-bold text-brand-700">
                        {formatCurrency(Number(extraPayment))}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Payoff Strategies */}
          {debts.length > 0 && (
            <div className="space-y-6">
              {/* Strategy Selection */}
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-6">
                  Payoff Strategy
                </h2>

                <div className="grid grid-cols-2 gap-4 mb-6">
                  <button
                    onClick={() => setSelectedStrategy('avalanche')}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      selectedStrategy === 'avalanche'
                        ? 'border-brand-500 bg-brand-50'
                        : 'border-gray-200 hover:border-brand-300'
                    }`}
                  >
                    <h3 className="font-medium text-gray-900 mb-1">Debt Avalanche</h3>
                    <p className="text-sm text-gray-600">
                      Highest interest first (saves money)
                    </p>
                  </button>

                  <button
                    onClick={() => setSelectedStrategy('snowball')}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      selectedStrategy === 'snowball'
                        ? 'border-brand-500 bg-brand-50'
                        : 'border-gray-200 hover:border-brand-300'
                    }`}
                  >
                    <h3 className="font-medium text-gray-900 mb-1">Debt Snowball</h3>
                    <p className="text-sm text-gray-600">
                      Smallest balance first (quick wins)
                    </p>
                  </button>
                </div>

                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <p className="text-sm text-gray-500 mb-1">Time to Debt-Free</p>
                      <p className="text-xl font-bold text-gray-900">
                        {strategy.monthsToPayoff} months
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 mb-1">Total Interest</p>
                      <p className="text-xl font-bold text-red-600">
                        {formatCurrency(strategy.totalInterest)}
                      </p>
                    </div>
                  </div>

                  {/* Payoff Chart */}
                  <div className="h-[300px] mt-6">
                    <ResponsiveContainer width="100%" height="100%">
                      <RechartsLineChart data={strategy.monthlyPayments}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis
                          dataKey="month"
                          tickLine={false}
                          axisLine={false}
                          tick={{ fontSize: 12, fill: '#6b7280' }}
                        />
                        <YAxis
                          tickFormatter={(value) => `£${(value / 1000).toFixed(0)}k`}
                          tickLine={false}
                          axisLine={false}
                          tick={{ fontSize: 12, fill: '#6b7280' }}
                        />
                        <Tooltip
                          formatter={(value: number) => formatCurrency(value)}
                          contentStyle={{
                            backgroundColor: 'rgba(255, 255, 255, 0.95)',
                            borderRadius: '8px',
                            border: 'none',
                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                          }}
                        />
                        <Legend />
                        <Line
                          type="monotone"
                          dataKey="remainingBalance"
                          name="Remaining Balance"
                          stroke="#ef4444"
                          strokeWidth={2}
                          dot={false}
                        />
                        <Line
                          type="monotone"
                          dataKey="payment"
                          name="Monthly Payment"
                          stroke="#3b82f6"
                          strokeWidth={2}
                          dot={false}
                        />
                      </RechartsLineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>

              {/* Tips */}
              <div className="bg-brand-50 rounded-xl p-6">
                <h3 className="font-semibold text-brand-900 mb-2">Tips for Success</h3>
                <ul className="text-sm text-brand-800 space-y-2">
                  <li>Stop taking on new debt while paying off existing debt</li>
                  <li>Consider balance transfer options for high-interest credit cards</li>
                  <li>Set up automatic payments to avoid missing due dates</li>
                  <li>Put any windfalls (tax returns, bonuses) towards debt</li>
                </ul>
              </div>
            </div>
          )}
        </div>
    </ToolPage>
  );
}